/*
  Admin › Stage Templates (29) — wave 1: idea 1 part 1 (a clear "template is out of date — new projects
  don't use it" callout, with what differs), 2 (compact table: # · Stage + description · Approval switch
  with a Gate / No approval label next to it), 5 (scope line "applies to new projects only" + a toast with
  Undo after every switch) and 9 (phones: a vertical list with 44px switch targets, no sideways scroll).
  Wave 2: ideas 1 ("Adopt live pipeline as template" — the template is used by new projects again; while it
  is out of date the table shows the live pipeline and its switches are locked), 3 (pipeline overview: the
  stages as a stepper under their phases, approval gates stamped, active projects per stage) and 4 (Edit
  pipeline: rename, describe, reorder by drag or keyboard, add and delete stages, saved once — the stages
  new projects rely on keep their names).
  Wave 3: ideas 6 (Approved by — who signs off each gate; copied onto new projects' stages), 7 (Active
  projects per stage — the number opens the list of those projects) and 8 (a template per system type: chips
  above the pipeline — Default for every system, "+ BMS template" copies the pipeline for BMS projects only).
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown, ArrowUp, BetweenHorizontalStart, Check, CheckCheck, ChevronDown, CircleMinus, GripVertical, Info, Lock,
  Plus, Stamp, Trash2, TriangleAlert, Workflow, X,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { UnsavedBar } from '@/components/ui/unsaved-bar';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { EmptyState } from '@/components/ui/empty-state';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SystemChip } from '@/components/ui/system-chip';
import { AdminCallout, AdminCard } from '@/components/admin/AdminUi';
import {
  FALLBACK_APPROVERS, REQUIRED_STAGE_NAMES, approvalToast, approverLabel, effectivePipeline, moveItem, newPipelineStage,
  pipelineChanges, pipelineDraft, pipelinePayload, pipelinePhases, pipelineProblems, stageSyncSummary,
  systemTemplateChips, withStagePatch, DEFAULT_APPROVAL_SLA_DAYS, STAGE_SLA_MAX_DAYS, stageSlaDays, stageSlaLabel,
} from '@/components/admin/adminModel';

const NO_APPROVER = '__none__';

/** 29 #6 — who signs off a gate. A select for gates, a dash otherwise. */
function ApproverSelect({ stageName, value, approvers, onChange, disabled, testId }) {
  return (
    <Select value={value || NO_APPROVER} onValueChange={(next) => onChange(next === NO_APPROVER ? '' : next)} disabled={disabled}>
      <SelectTrigger
        className={cn(
          // a long approver ("Internal · Senior Site Engineer") ends in "…" before the chevron, never under it
          'h-[30px] w-full min-w-0 gap-1.5 rounded-control border-line bg-surface px-2.5 text-[12.5px] [&>span]:!block [&>span]:min-w-0 [&>span]:truncate [&>span]:text-left',
          value ? 'text-ink' : 'text-status-amber-ink'
        )}
        title={value ? approverLabel(approvers, value) : undefined}
        aria-label={`${stageName}: approved by`}
        data-testid={testId}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_APPROVER} className="text-ink-3">Not set — pick who approves</SelectItem>
        {approvers.map((option) => (
          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** 29 #7 — active projects standing in a stage; the number opens the list (each links to its Stages tab). */
function ActiveProjectsCell({ stageName, projects = [], count = 0, index, isNew = false }) {
  if (isNew) return <span className="text-[13px] text-ink-4" aria-label="New stage — no projects yet">—</span>;
  if (!count) return <span className="text-[13px] tabular-nums text-ink-4" aria-label="No active projects">0</span>;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded px-1 text-[13px] font-semibold tabular-nums text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`${count} active ${count === 1 ? 'project' : 'projects'} in ${stageName} — show them`}
          data-testid={`stage-projects-${index}`}
        >
          {formatNumber(count)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] rounded-xl border-line bg-surface p-0 text-ink shadow-overlay" data-testid={`stage-projects-list-${index}`}>
        <p className="border-b border-line-2 px-3.5 py-2.5 text-[12.5px] font-bold text-ink">
          {count} active {count === 1 ? 'project' : 'projects'} in {stageName}
        </p>
        <ul className="max-h-[280px] overflow-y-auto py-1">
          {projects.map((project) => (
            <li key={project.id}>
              <Link
                to={`/projects/${project.id}?tab=stages`}
                className="flex items-center gap-2 px-3.5 py-1.5 text-[13px] text-ink hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
              >
                <SystemChip systemType={project.system_type} size="xs" />
                <span className="min-w-0 flex-1 truncate">{project.name}</span>
              </Link>
            </li>
          ))}
        </ul>
        {projects.length < count ? <p className="border-t border-line-2 px-3.5 py-2 text-[12px] text-ink-3">+{count - projects.length} more</p> : null}
      </PopoverContent>
    </Popover>
  );
}

/** 29 #8 — which template the page shows: Default (every system) or one system type's own. Small system
    types (fewer than MIN_CHIP_PROJECTS projects, no template yet) wait behind "Other · N". */
const MIN_CHIP_PROJECTS = 3;
function TemplateScopeRow({ chips, scope, onScope, onCreate, busyKey }) {
  const own = chips.chips.filter((chip) => chip.template);
  const defaultCount = chips.total - own.reduce((sum, chip) => sum + chip.projects, 0);
  const main = chips.chips.filter((chip) => chip.template || chip.projects >= MIN_CHIP_PROJECTS);
  const minor = chips.chips.filter((chip) => !main.includes(chip));
  const minorCount = minor.reduce((sum, chip) => sum + chip.projects, 0) + chips.unassigned;
  const chipClass = 'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  const addClass = cn(chipClass, 'border-dashed border-[color:var(--line-dashed)] bg-surface text-ink-2 hover:border-[color:var(--ink-4)] hover:text-ink disabled:opacity-60');
  let firstAdd = true;
  const addLabel = (chip) => {
    const label = firstAdd ? `${chip.systemType} template · ${formatNumber(chip.projects)} projects` : `${chip.systemType} · ${formatNumber(chip.projects)}`;
    firstAdd = false;
    return label;
  };
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Stage template" data-testid="stage-template-scope-row">
      <span className="mr-0.5 text-[12.5px] text-ink-3">Template</span>
      <button
        type="button"
        onClick={() => onScope('')}
        aria-pressed={!scope}
        className={cn(chipClass, !scope ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line bg-surface text-ink-2 hover:bg-surface-hover')}
        data-testid="stage-scope-default"
      >
        <Workflow className="h-[13px] w-[13px]" aria-hidden="true" />
        Default · {own.length ? 'other systems' : 'all systems'} · {formatNumber(defaultCount)} projects
      </button>
      {main.map((chip) => (chip.template ? (
        <button
          key={chip.key}
          type="button"
          onClick={() => onScope(chip.key)}
          aria-pressed={scope === chip.key}
          className={cn(chipClass, scope === chip.key ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line bg-surface text-ink-2 hover:bg-surface-hover')}
          data-testid={`stage-scope-${chip.key}`}
        >
          <SystemChip systemType={chip.systemType} size="xs" />
          {chip.systemType} template · {formatNumber(chip.projects)}
        </button>
      ) : (
        <button
          key={chip.key}
          type="button"
          onClick={() => onCreate(chip)}
          disabled={busyKey === chip.key}
          className={addClass}
          title={`Give ${chip.systemType} projects their own stage template`}
          aria-label={`Add a ${chip.systemType} template (${chip.projects} projects)`}
          data-testid={`stage-scope-add-${chip.key}`}
        >
          <Plus className="h-[13px] w-[13px]" aria-hidden="true" />
          {addLabel(chip)}
        </button>
      )))}
      {minorCount ? (
        minor.length ? (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className={cn(chipClass, 'border-line bg-surface text-ink-3 hover:bg-surface-hover')} data-testid="stage-scope-other">
                Other · {formatNumber(minorCount)}
                <ChevronDown className="h-3 w-3" aria-hidden="true" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[260px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
              <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Smaller systems — default template</p>
              {minor.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => onCreate(chip)}
                  className="flex h-[34px] w-full items-center gap-2 rounded-lg px-2.5 text-left text-[13px] hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
                  data-testid={`stage-scope-add-${chip.key}`}
                >
                  <SystemChip systemType={chip.systemType} size="xs" />
                  <span className="min-w-0 flex-1 truncate">{chip.systemType} · {chip.projects}</span>
                  <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-ink"><Plus className="h-3 w-3" aria-hidden="true" />Template</span>
                </button>
              ))}
              {chips.unassigned ? <p className="px-2.5 py-1.5 text-[12px] text-ink-3">{chips.unassigned} without a system type — always the default.</p> : null}
            </PopoverContent>
          </Popover>
        ) : (
          <span className="text-[12px] text-ink-3" title="Projects without a system type always use the default template">No system · {formatNumber(chips.unassigned)}</span>
        )
      ) : null}
      <StatusPill tone="lime" label="New" size="sm" hideDot />
    </div>
  );
}

const NAME_MAX = 80;

function ApprovalLabel({ on }) {
  return on ? (
    <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink"><Stamp className="h-[13px] w-[13px]" aria-hidden="true" /> Gate</span>
  ) : (
    <span className="text-[13px] text-ink-3">No approval</span>
  );
}

function DiffList({ sync }) {
  const rows = [
    sync.added?.length ? { label: 'New in the live pipeline', items: sync.added } : null,
    sync.renamed?.length ? { label: 'Renamed', items: sync.renamed.map((item) => `${item.from} → ${item.to}`) } : null,
    sync.dropped?.length ? { label: 'Only in the saved template', items: sync.dropped, struck: true } : null,
  ].filter(Boolean);
  return (
    <dl className="mt-2.5 grid gap-x-4 gap-y-1.5 text-[12.5px] sm:grid-cols-[200px_minmax(0,1fr)]" data-testid="stage-sync-diff">
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <dt className="font-semibold text-ink-2">{row.label}</dt>
          <dd className="flex flex-wrap gap-1.5">
            {row.items.map((item) => (
              <span key={item} className={cn('rounded-md border border-line bg-surface px-1.5 text-[12px] text-ink', row.struck && 'text-ink-3 line-through')}>
                {item}
              </span>
            ))}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** 29 #3 — the pipeline as a stepper under its phases; gates stamped; active projects under each stage. */
function PipelineOverview({ stages, counts = {}, outOfDate }) {
  const phases = pipelinePhases(stages);
  const gates = stages.filter((stage) => stage.required_approval).length;
  // draft rows (Edit pipeline) carry `origin`: counts belong to the stage a row came from, renamed or not
  const keyOf = (stage) => (stage.origin !== undefined ? stage.origin : stage.name);
  const names = new Set(stages.map(keyOf));
  const elsewhere = Object.entries(counts).filter(([name]) => !names.has(name));
  const elsewhereTotal = elsewhere.reduce((sum, [, count]) => sum + count, 0);
  const columns = { gridTemplateColumns: `repeat(${stages.length}, minmax(96px, 1fr))` };
  return (
    <AdminCard
      title="Project pipeline"
      subtitle={outOfDate ? 'What new projects get today — the live pipeline' : 'What new projects get'}
      aside={(
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-2" data-testid="pipeline-legend">
          <span className="inline-flex items-center gap-1"><Stamp className="h-[13px] w-[13px] text-brand-ink" aria-hidden="true" /> Approval gate · <b className="font-bold text-ink">{gates}</b></span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full border-[1.5px] border-[color:var(--ink-4)]" aria-hidden="true" /> No approval · <b className="font-bold text-ink">{stages.length - gates}</b></span>
        </span>
      )}
      bodyClassName="py-5"
      data-testid="pipeline-overview"
    >
      <div className="-mx-1 overflow-x-auto px-1 pb-1">
        <div className="min-w-[760px]">
          <div className="grid" style={columns} aria-hidden="true">
            {phases.map((phase) => (
              <div key={`${phase.key}-${phase.from}`} className="px-2 text-center" style={{ gridColumn: `${phase.from} / ${phase.to + 1}` }}>
                <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
                  {phase.label} <span className="font-semibold text-ink-4">{phase.from === phase.to ? phase.from : `${phase.from}–${phase.to}`}</span>
                </p>
                <span className="mt-1.5 block h-1.5 rounded-t border-x border-t border-line" />
              </div>
            ))}
          </div>
          <ol className="mt-2 grid" style={columns} aria-label="Stages in order">
            {stages.map((stage, index) => {
              const gate = Boolean(stage.required_approval);
              const count = stage.isNew ? 0 : counts[keyOf(stage)] || 0;
              const phase = phases.find((item) => index + 1 >= item.from && index + 1 <= item.to);
              return (
                <li key={`${stage.name}-${index}`} className="relative flex flex-col items-center px-1.5 text-center" data-testid={`pipeline-step-${index}`}>
                  {index > 0 ? <span className="absolute left-0 right-1/2 top-[14px] h-px bg-line" aria-hidden="true" /> : null}
                  {index < stages.length - 1 ? <span className="absolute left-1/2 right-0 top-[14px] h-px bg-line" aria-hidden="true" /> : null}
                  <span
                    className={cn(
                      'relative z-[1] grid h-7 w-7 place-items-center rounded-full border-[1.5px] bg-surface text-[11.5px] font-bold tabular-nums',
                      gate ? 'border-brand text-brand-ink' : 'border-line text-ink-3'
                    )}
                    aria-hidden="true"
                  >
                    {gate ? <Stamp className="h-[13px] w-[13px]" /> : index + 1}
                  </span>
                  <span className="mt-2 text-[12.5px] font-semibold leading-tight text-ink">{stage.name}</span>
                  <span className="sr-only">
                    {`Stage ${index + 1}${phase ? `, ${phase.label}` : ''}: ${gate ? 'approval gate' : 'no approval'}${count ? `, ${count} active ${count === 1 ? 'project' : 'projects'} here now` : ''}`}
                  </span>
                  {count ? (
                    <span className="mt-1.5 rounded-full bg-status-blue-bg px-2 text-[11px] font-bold leading-5 text-status-blue-ink" aria-hidden="true" data-testid={`pipeline-count-${index}`}>
                      {formatNumber(count)} {count === 1 ? 'project' : 'projects'}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
      {elsewhereTotal ? (
        <p className="mt-3 flex items-start gap-1.5 text-[12px] text-ink-3" data-testid="pipeline-elsewhere">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {elsewhereTotal} active {elsewhereTotal === 1 ? 'project is' : 'projects are'} on a stage outside this pipeline ({elsewhere.map(([name]) => name).join(', ')}) — older projects keep their own stages.
        </p>
      ) : null}
    </AdminCard>
  );
}

export default function StagesTab({ stageTemplate, setStageTemplate: setDefaultTemplate, loading, command, onEditingChange, onDirtyChange }) {
  const confirm = useConfirm();
  // 29 #8: '' = the default template; otherwise a system type's own template (loaded on demand)
  const [scope, setScopeState] = useState('');
  const [systemDoc, setSystemDoc] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeBusy, setScopeBusy] = useState('');
  const [showDiff, setShowDiff] = useState(false);
  const [savingIndex, setSavingIndex] = useState(-1);
  const [adopting, setAdopting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [status, setStatus] = useState('idle');
  const [saveError, setSaveError] = useState('');
  const [dragKey, setDragKey] = useState('');
  const [leftKeys, setLeftKeys] = useState(() => new Set());
  const [overKey, setOverKey] = useState('');
  const focusKeyRef = useRef('');
  const nameRefs = useRef({});

  const isSystem = Boolean(scope);
  const chips = useMemo(
    () => systemTemplateChips(stageTemplate?.system_type_counts, stageTemplate?.system_templates),
    [stageTemplate?.system_type_counts, stageTemplate?.system_templates],
  );
  const scopeChip = chips.chips.find((chip) => chip.key === scope) || null;
  const template = isSystem ? systemDoc : stageTemplate;
  const setStageTemplate = isSystem ? setSystemDoc : setDefaultTemplate;
  const approvers = stageTemplate?.approvers?.length ? stageTemplate.approvers : FALLBACK_APPROVERS;
  const sync = isSystem ? null : stageTemplate?.sync || null;
  const summary = stageSyncSummary(sync);
  const outOfDate = Boolean(summary?.outOfDate);
  const pipeline = useMemo(
    () => (isSystem ? (Array.isArray(systemDoc?.stages) ? systemDoc.stages : []) : effectivePipeline(stageTemplate)),
    [isSystem, systemDoc, stageTemplate],
  );
  const stages = pipeline;
  const activeProjects = isSystem ? scopeChip?.active : stageTemplate?.active_project_count;
  // active projects per stage — for a system template only that system's projects
  const stageProjects = useMemo(() => {
    const all = stageTemplate?.stage_projects || {};
    if (!isSystem) return all;
    const key = scope;
    return Object.fromEntries(Object.entries(all).map(([name, list]) => [
      name, list.filter((project) => String(project.system_type || '').trim().toLowerCase().replace(/\s+/g, ' ') === key),
    ]));
  }, [stageTemplate?.stage_projects, isSystem, scope]);
  const counts = useMemo(() => {
    if (!isSystem) return stageTemplate?.stage_project_counts || {};
    return Object.fromEntries(Object.entries(stageProjects).map(([name, list]) => [name, list.length]).filter(([, count]) => count));
  }, [isSystem, stageTemplate?.stage_project_counts, stageProjects]);
  const required = stageTemplate?.required_stage_names || REQUIRED_STAGE_NAMES;
  const lastSaved = template?.updated_at || template?.created_at;
  const scopeLabel = isSystem ? `${scopeChip?.systemType || scope} projects` : 'new projects';
  // In-use template, but create_project resets these stages to the built-in approval setting (none today).
  const overridden = new Map(outOfDate ? [] : (summary?.approvalMismatches || []).map((item) => [item.name, item]));
  const renamedFrom = new Map((sync?.renamed || []).map((item) => [item.to, item.from]));
  const added = new Set(sync?.added || []);

  const changes = useMemo(() => (editing ? pipelineChanges(stages, draft) : []), [editing, stages, draft]);
  const problems = useMemo(() => (editing ? pipelineProblems(draft) : {}), [editing, draft]);
  const hasProblems = Object.keys(problems).length > 0;
  const shownProblem = (row) => (problems[row.key] && (!row.isNew || leftKeys.has(row.key)) ? problems[row.key] : '');
  const anyShownProblem = editing && draft.some((row) => shownProblem(row));
  const dirtyCount = editing ? changes.length : 0;

  useEffect(() => { onDirtyChange?.(dirtyCount); }, [dirtyCount, onDirtyChange]);
  useEffect(() => { onEditingChange?.(editing); }, [editing, onEditingChange]);
  useEffect(() => () => { onDirtyChange?.(0); onEditingChange?.(false); }, [onDirtyChange, onEditingChange]);

  // Put the caret in a stage that was just added.
  useEffect(() => {
    if (!focusKeyRef.current) return;
    const node = nameRefs.current[focusKeyRef.current];
    focusKeyRef.current = '';
    node?.focus();
    node?.select?.();
  }, [draft]);

  const startEditing = useCallback((apply) => {
    const rows = pipelineDraft(stages);
    setDraft(apply ? apply(rows) : rows);
    setEditing(true);
    setStatus('idle');
    setSaveError('');
  }, [stages]);

  const loadSystem = useCallback(async (key) => {
    const res = await api.get(`/admin/stage-templates/system/${encodeURIComponent(key)}`);
    setSystemDoc(res.data);
    return res.data;
  }, []);

  const setScope = async (key) => {
    if (key === scope) return;
    if (editing && changes.length) {
      const ok = await confirm({
        title: 'Leave the pipeline you are editing?',
        description: 'Your unsaved pipeline changes will be lost.',
        confirmLabel: 'Discard changes',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setEditing(false);
    setDraft([]);
    setStatus('idle');
    setSaveError('');
    setShowDiff(false);
    if (!key) {
      setScopeState('');
      setSystemDoc(null);
      return;
    }
    setScopeLoading(true);
    setScopeState(key);
    setSystemDoc(null);
    try {
      await loadSystem(key);
    } catch (err) {
      notify.error('Could not open that template', { description: getErrorMessage(err) });
      setScopeState('');
    } finally {
      setScopeLoading(false);
    }
  };

  const refreshDefault = async () => {
    const fresh = await api.get('/admin/stage-templates');
    setDefaultTemplate(fresh.data);
  };

  /** "+ BMS template": a copy of the pipeline new projects get today, for that system type only. */
  const createSystemTemplate = async (chip) => {
    const source = effectivePipeline(stageTemplate);
    let created = null;
    const ok = await confirm({
      title: `Give ${chip.systemType} projects their own stage template?`,
      subtitle: `${formatNumber(chip.projects)} ${chip.projects === 1 ? 'project' : 'projects'} today · ${chip.active} active`,
      description: `It starts as a copy of the default pipeline (${source.length} stages). Then:`,
      children: (
        <ul className="list-disc space-y-1 pl-5 text-[13px] text-ink-2" data-testid="system-template-consequences">
          <li>New {chip.systemType} projects start from this template</li>
          <li>Existing {chip.systemType} projects keep their own stages</li>
          <li>Other systems keep using the default template</li>
        </ul>
      ),
      confirmLabel: `Create ${chip.systemType} template`,
      icon: Workflow,
      onConfirm: async () => {
        setScopeBusy(chip.key);
        try {
          const stagesCopy = source.map((stage, index) => ({ ...stage, order: index + 1 }));
          created = (await api.put(`/admin/stage-templates/system/${encodeURIComponent(chip.key)}`, {
            system_type: chip.systemType, stages: stagesCopy,
          })).data;
        } finally {
          setScopeBusy('');
        }
      },
    });
    if (!ok || !created) return;
    await refreshDefault().catch(() => {});
    setScopeState(chip.key);
    setSystemDoc(created);
    const undoId = created.undo_id;
    notify.success(`${chip.systemType} template created — edit it below`, {
      description: `New ${chip.systemType} projects use it from now on.`,
      ...(undoId ? {
        undo: async () => {
          await api.post(`/undo/entries/${undoId}`);
          setScopeState('');
          setSystemDoc(null);
          await refreshDefault();
        },
        undoneMessage: `${chip.systemType} projects use the default template again`,
      } : {}),
    });
  };

  const removeSystemTemplate = async () => {
    if (!scopeChip) return;
    let removed = null;
    const ok = await confirm({
      title: `Remove the ${scopeChip.systemType} template?`,
      description: `New ${scopeChip.systemType} projects will start from the default template again. Existing projects keep their stages.`,
      confirmLabel: 'Remove template',
      tone: 'danger',
      onConfirm: async () => {
        removed = (await api.delete(`/admin/stage-templates/system/${encodeURIComponent(scope)}`)).data;
      },
    });
    if (!ok) return;
    const label = scopeChip.systemType;
    setScopeState('');
    setSystemDoc(null);
    await refreshDefault().catch(() => {});
    const undoId = removed?.undo_id;
    notify.success(`${label} template removed`, {
      description: `New ${label} projects use the default template.`,
      ...(undoId ? { undo: async () => { await api.post(`/undo/entries/${undoId}`); await refreshDefault(); }, undoneMessage: `${label} template is back` } : {}),
    });
  };

  /** One PUT for the template on screen (default or system). */
  const putStages = (list) => (isSystem
    ? api.put(`/admin/stage-templates/system/${encodeURIComponent(scope)}`, { system_type: scopeChip?.systemType || systemDoc?.system_type || scope, stages: list })
    : api.put('/admin/stage-templates', { stages: list }));

  const addStage = useCallback((afterIndex) => {
    const row = newPipelineStage('');
    focusKeyRef.current = row.key;
    const insert = (rows) => {
      const copy = [...rows];
      // a new stage goes before "Handing over documents" (the last stage) unless placed explicitly
      const handover = copy.findIndex((item) => item.origin === 'Handing over documents');
      const at = afterIndex === undefined ? (handover === -1 ? copy.length : handover) : Math.min(afterIndex + 1, copy.length);
      copy.splice(at, 0, row);
      return copy;
    };
    if (editing) setDraft((rows) => insert(rows));
    else startEditing(insert);
  }, [editing, startEditing]);

  // Header buttons (AdminPage) → "edit" / "add".
  const lastCommand = useRef(command?.n);
  useEffect(() => {
    if (!command || command.n === lastCommand.current) return;
    lastCommand.current = command.n;
    if (command.type === 'add') addStage();
    else if (!editing) startEditing();
  }, [command, addStage, editing, startEditing]);

  const updateRow = (key, patch) => {
    setStatus((current) => (current === 'saving' ? current : 'idle'));
    setDraft((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };
  const move = (from, to) => setDraft((rows) => moveItem(rows, from, to));

  const discard = () => {
    setEditing(false);
    setDraft([]);
    setStatus('idle');
    setSaveError('');
    setLeftKeys(new Set());
  };

  const merge = (data) => setStageTemplate((current) => ({
    ...current,
    stages: data?.stages || current?.stages,
    updated_at: data?.updated_at || current?.updated_at,
    updated_by: data?.updated_by || current?.updated_by,
    sync: data?.sync || current?.sync,
  }));

  const refetch = async () => {
    if (isSystem) {
      await loadSystem(scope);
      await refreshDefault().catch(() => {});
      return;
    }
    await refreshDefault();
  };

  const savePipeline = async () => {
    if (hasProblems || !changes.length) return;
    setStatus('saving');
    setSaveError('');
    try {
      const res = await putStages(pipelinePayload(draft, stages));
      merge(res.data);
      if (isSystem) refreshDefault().catch(() => {});
      const undoId = res.data?.undo_id;
      const wasOutOfDate = outOfDate;
      const lines = changes.map((change) => change.text);
      setEditing(false);
      setDraft([]);
      setStatus('saved');
      notify.success(wasOutOfDate ? 'Pipeline saved — new projects use it from now on' : `Pipeline saved for ${scopeLabel}`, {
        description: lines.slice(0, 4).join(' · ') + (lines.length > 4 ? ` · +${lines.length - 4} more` : ''),
        ...(undoId ? { undo: async () => { await api.post(`/undo/entries/${undoId}`); await refetch(); }, undoneMessage: 'Pipeline change undone' } : {}),
      });
    } catch (err) {
      setStatus('error');
      setSaveError(getErrorMessage(err, 'Could not save the pipeline'));
    }
  };

  const adopt = async () => {
    let result = null;
    const ok = await confirm({
      title: 'Adopt the live pipeline as the template?',
      subtitle: `${summary?.liveCount || stages.length} stages · ${summary?.differences || 0} differences from the saved template`,
      description: 'New projects already get these stages. Once adopted, the switches and edits on this page reach new projects. What happens:',
      children: (
        <ul className="space-y-1.5 text-[13px] text-ink-2" data-testid="adopt-consequences">
          {sync?.added?.length ? <li><b className="font-semibold text-ink">Comes in:</b> {sync.added.join(', ')}</li> : null}
          {sync?.renamed?.length ? <li><b className="font-semibold text-ink">Renamed:</b> {sync.renamed.map((item) => `${item.from} → ${item.to}`).join(', ')}</li> : null}
          {sync?.dropped?.length ? <li><b className="font-semibold text-ink">Leaves the template:</b> {sync.dropped.join(', ')} <span className="text-ink-3">— handled in Procurement</span></li> : null}
          <li><b className="font-semibold text-ink">Existing projects</b> keep their own stages{Number.isFinite(activeProjects) ? ` (${formatNumber(activeProjects)} active)` : ''}.</li>
        </ul>
      ),
      confirmLabel: 'Adopt live pipeline',
      icon: CheckCheck,
      onConfirm: async () => {
        setAdopting(true);
        try {
          result = (await api.post('/admin/stage-templates/adopt-live')).data;
        } finally {
          setAdopting(false);
        }
      },
    });
    if (!ok || !result) return;
    merge(result);
    setShowDiff(false);
    const undoId = result.undo_id;
    notify.success('Live pipeline adopted — new projects use this template', {
      description: 'Approval switches and edits here now reach new projects.',
      ...(undoId ? { undo: async () => { await api.post(`/undo/entries/${undoId}`); await refetch(); }, undoneMessage: 'The old template is back' } : {}),
    });
  };

  /** One stage's approval switch or approver, saved at once (toast with Undo). */
  const patchStage = async (index, patch, toast) => {
    if (!template || savingIndex !== -1 || outOfDate) return;
    const previous = template;
    const updated = withStagePatch(stages, index, patch);
    setSavingIndex(index);
    setStageTemplate({ ...template, stages: updated });
    try {
      const res = await putStages(updated);
      setStageTemplate((current) => ({
        ...current,
        stages: res.data?.stages || updated,
        updated_at: res.data?.updated_at || current?.updated_at,
        sync: res.data?.sync || current?.sync,
      }));
      const undoId = res.data?.undo_id;
      const { title, description } = toast(res.data);
      notify.success(title, {
        description,
        ...(undoId ? { undo: async () => { await api.post(`/undo/entries/${undoId}`); await refetch(); } } : {}),
      });
    } catch (err) {
      setStageTemplate(previous);
      notify.error('Could not update the stage template', { description: getErrorMessage(err) });
    } finally {
      setSavingIndex(-1);
    }
  };

  const changeApprover = (index, value) => {
    const stage = stages[index];
    patchStage(index, { approved_by: value }, () => ({
      title: value ? `${stage.name}: approved by ${approverLabel(approvers, value)}` : `${stage.name}: approver cleared`,
      description: `For ${scopeLabel} — projects created from now on wait for this approver.`,
    }));
  };

  const toggleApproval = async (index) => {
    if (!template || savingIndex !== -1 || outOfDate) return;
    const previous = template;
    const stage = stages[index];
    const nextValue = !stage.required_approval;
    const updated = withStagePatch(stages, index, { required_approval: nextValue });
    setSavingIndex(index);
    setStageTemplate({ ...template, stages: updated });
    try {
      const res = await putStages(updated);
      setStageTemplate((current) => ({
        ...current,
        stages: res.data?.stages || updated,
        updated_at: res.data?.updated_at || current?.updated_at,
        sync: res.data?.sync || current?.sync,
      }));
      const undoId = res.data?.undo_id;
      const { title, description } = approvalToast(stage.name, nextValue, res.data?.sync);
      notify.success(title, {
        description,
        ...(undoId ? {
          undo: async () => {
            await api.post(`/undo/entries/${undoId}`);
            await refetch();
          },
        } : {}),
      });
    } catch (err) {
      setStageTemplate(previous);
      notify.error('Could not update the stage template', { description: getErrorMessage(err) });
    } finally {
      setSavingIndex(-1);
    }
  };

  if ((loading && !stageTemplate) || (isSystem && scopeLoading && !systemDoc)) {
    return (
      <div className="space-y-2 rounded-card border border-line bg-surface p-4" aria-busy="true" aria-label="Loading stages">
        {[0, 1, 2, 3].map((index) => <div key={index} className="h-11 animate-pulse rounded-lg bg-surface-2" />)}
      </div>
    );
  }

  const gates = stages.filter((stage) => stage.required_approval).length;
  const rows = editing ? draft : stages.map((stage, index) => ({ key: `${stage.name}-${index}`, origin: stage.name, ...stage }));

  const confirmDelete = (name) => confirm({
    title: `Delete “${name}” from the pipeline?`,
    subtitle: 'Only for new projects',
    description: `New projects won’t get this stage.${Number.isFinite(activeProjects) ? ` The ${formatNumber(activeProjects)} active projects keep their own stages.` : ''} Nothing changes until you save the pipeline.`,
    tone: 'danger',
    confirmLabel: 'Delete stage',
  });
  // In view mode each item opens Edit pipeline with that change already made.
  const editThen = (updater) => {
    if (editing) setDraft((list) => updater(list));
    else startEditing(updater);
  };
  const rowMenu = (row, index) => {
    const isRequired = required.includes(row.origin);
    return [
      { label: 'Move up', icon: ArrowUp, disabled: index === 0, onSelect: () => editThen((list) => moveItem(list, index, index - 1)), testId: `stage-up-${index}` },
      { label: 'Move down', icon: ArrowDown, disabled: index === rows.length - 1, onSelect: () => editThen((list) => moveItem(list, index, index + 1)), testId: `stage-down-${index}` },
      { label: 'Insert stage below', icon: BetweenHorizontalStart, onSelect: () => addStage(index), testId: `stage-insert-${index}` },
      {
        label: 'Delete stage…', icon: Trash2, danger: true, disabled: isRequired, hint: isRequired ? 'Kept' : 'asks first', testId: `stage-delete-${index}`,
        onSelect: async () => {
          if (isRequired) return;
          if (!row.isNew && !(await confirmDelete(row.origin || row.name))) return;
          editThen((list) => list.filter((item) => (editing ? item.key !== row.key : item.origin !== row.origin)));
        },
      },
    ];
  };

  const outOfDateCallout = outOfDate ? (
    <AdminCallout
      tone="amber"
      icon={TriangleAlert}
      title="This template is out of date — new projects don’t use it"
      data-testid="stage-template-out-of-date"
      actions={(
        <>
          <Button type="button" variant="primary" size="sm" onClick={adopt} disabled={adopting} data-testid="stage-template-adopt">
            <CheckCheck aria-hidden="true" /> {adopting ? 'Adopting…' : 'Adopt live pipeline as template'}
          </Button>
          <button
            type="button"
            onClick={() => setShowDiff((current) => !current)}
            aria-expanded={showDiff}
            className="inline-flex items-center gap-1 rounded text-[13px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="stage-template-what-changed"
          >
            What changed?
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showDiff && 'rotate-180')} aria-hidden="true" />
          </button>
        </>
      )}
    >
      <p>
        New projects get the built-in {summary.liveCount}-stage pipeline shown below.{' '}
        <b className="font-semibold text-ink">
          {summary.differences} {summary.differences === 1 ? 'difference' : 'differences'}
        </b>
        {summary.breakdown ? ` (${summary.breakdown})` : ''} from the saved template, so approval switches changed here have no effect on projects yet.
        Adopt the live pipeline so the switches and edits here take effect for new projects.
      </p>
      {showDiff ? <DiffList sync={sync} /> : null}
    </AdminCallout>
  ) : null;

  const onDragStart = (event, key) => {
    setDragKey(key);
    event.dataTransfer.effectAllowed = 'move';
    try { event.dataTransfer.setData('text/plain', key); } catch { /* old browsers */ }
  };
  const onDrop = (event, key) => {
    event.preventDefault();
    const from = draft.findIndex((row) => row.key === dragKey);
    const to = draft.findIndex((row) => row.key === key);
    if (from !== -1 && to !== -1) move(from, to);
    setDragKey('');
    setOverKey('');
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4">
        {outOfDateCallout}
        {stageTemplate?.system_type_counts ? (
          <TemplateScopeRow chips={chips} scope={scope} onScope={setScope} onCreate={createSystemTemplate} busyKey={scopeBusy} />
        ) : null}
        {isSystem && scopeChip ? (
          <AdminCallout
            tone="navy"
            icon={Info}
            title={`${scopeChip.systemType} template — new ${scopeChip.systemType} projects start from this pipeline`}
            data-testid="stage-system-template-note"
            actions={(
              <Button type="button" variant="outline" size="sm" className="bg-surface" onClick={removeSystemTemplate} data-testid="stage-system-template-remove">
                <X aria-hidden="true" /> Remove template
              </Button>
            )}
          >
            <p>
              {formatNumber(scopeChip.projects)} {scopeChip.projects === 1 ? 'project uses' : 'projects use'} {scopeChip.systemType} today; they keep their own stages.
              Other systems use the default template. Edits here only reach {scopeChip.systemType} projects created from now on.
            </p>
          </AdminCallout>
        ) : null}
        {!outOfDate && summary?.approvalMismatches?.length ? (
          <AdminCallout tone="navy" icon={Info} title="Some approval switches are not applied yet" data-testid="stage-template-mismatch">
            <p>
              New projects keep the built-in approval setting for {summary.approvalMismatches.map((item) => item.name).join(', ')}.
            </p>
          </AdminCallout>
        ) : null}

        {stages.length ? <PipelineOverview stages={editing ? draft : stages} counts={counts} outOfDate={outOfDate} /> : null}

        <section className="rounded-card border border-line bg-surface shadow-card" aria-labelledby="stages-heading">
          <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-4 py-3.5 sm:px-5">
            <h2 id="stages-heading" className="text-[15px] font-bold text-ink">Stages</h2>
            <p className="text-[13px] text-ink-3" data-testid="stage-template-scope">
              {isSystem ? `Applies to new ${scopeChip?.systemType || ''} projects only` : 'Applies to new projects only'}
              {Number.isFinite(activeProjects) ? ` · ${formatNumber(activeProjects)} active projects keep their own stages` : ' · existing projects keep their own stages'}
              {lastSaved ? ` · last saved ${formatDate(lastSaved)}` : ''}
            </p>
            {editing ? (
              <span className="ml-auto flex items-center gap-2">
                <StatusPill tone="amber" label="Editing pipeline" size="sm" />
                <Button type="button" variant="outline" size="sm" onClick={() => addStage()} data-testid="stage-add-inline">
                  <Plus aria-hidden="true" /> Add stage
                </Button>
                {!changes.length ? (
                  <Button type="button" variant="ghost" size="sm" onClick={discard} data-testid="stage-edit-done">Done</Button>
                ) : null}
              </span>
            ) : stages.length ? null : (
              <p className="ml-auto inline-flex items-center gap-3 text-[12px] text-ink-2">
                <span className="inline-flex items-center gap-1"><Stamp className="h-[13px] w-[13px]" aria-hidden="true" /> Approval gate · {gates}</span>
                <span>No approval · {stages.length - gates}</span>
              </p>
            )}
          </header>

          {!rows.length ? (
            <EmptyState icon={Workflow} title="No stages in the template" text="New projects get the built-in pipeline." />
          ) : (
            <table className="block w-full border-collapse md:table md:table-fixed" data-testid="stages-table">
              <colgroup>
                {editing ? <col className="w-[36px]" /> : null}
                <col className="w-[52px]" />
                <col />
                <col className="w-[180px]" />
                <col className="w-[230px]" />
                <col className="w-[124px]" />
                <col className="w-[120px]" />
                <col className="w-[52px]" />
              </colgroup>
              <thead className="hidden md:table-header-group">
                <tr className="border-b border-line">
                  {editing ? <th scope="col" className="h-9 bg-surface-2"><span className="sr-only">Drag to reorder</span></th> : null}
                  <th scope="col" className="h-9 bg-surface-2 px-4 text-left text-[11.5px] font-semibold text-ink-3">#</th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3">Stage</th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3">
                    Approval
                    {outOfDate && !editing ? (
                      <span className="ml-1.5 inline-flex items-center gap-1 font-normal text-ink-3" data-testid="stage-approval-locked">
                        <Lock className="h-3 w-3" aria-hidden="true" /> Locked until adopted
                      </span>
                    ) : null}
                  </th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3" data-testid="stage-approved-by-head">Approved by</th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3" data-testid="stage-sla-head">
                    <span title={`Days a stage may take before the project's Stages tab flags it. Empty: approval stages ${DEFAULT_APPROVAL_SLA_DAYS} days, others none. 0 = no SLA.`}>SLA (days)</span>
                  </th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-right text-[11.5px] font-semibold text-ink-3" data-testid="stage-active-head">
                    <span title={isSystem ? `Active ${scopeChip?.systemType || ''} projects whose current stage is this one` : 'Active projects whose current stage is this one'}>Active projects</span>
                  </th>
                  <th scope="col" className="h-9 bg-surface-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="block md:table-row-group">
                {rows.map((row, index) => {
                  const on = Boolean(row.required_approval);
                  const isRequired = required.includes(row.origin);
                  const override = !editing ? overridden.get(row.name) : null;
                  const problem = shownProblem(row);
                  const lockSwitch = !editing && outOfDate;
                  return (
                    <tr
                      key={row.key}
                      className={cn(
                        'flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line-2 px-4 py-2 last:border-b-0 md:table-row md:p-0',
                        !editing && 'hover:bg-surface-hover',
                        editing && overKey === row.key && dragKey && dragKey !== row.key && 'bg-brand-soft',
                        editing && dragKey === row.key && 'opacity-50'
                      )}
                      onDragOver={editing ? (event) => { event.preventDefault(); setOverKey(row.key); } : undefined}
                      onDrop={editing ? (event) => onDrop(event, row.key) : undefined}
                      data-testid={`stage-row-${index}`}
                    >
                      {editing ? (
                        <td className="shrink-0 md:pl-2 md:align-middle">
                          <button
                            type="button"
                            draggable
                            onDragStart={(event) => onDragStart(event, row.key)}
                            onDragEnd={() => { setDragKey(''); setOverKey(''); }}
                            onKeyDown={(event) => {
                              if (event.key === 'ArrowUp' && index > 0) { event.preventDefault(); move(index, index - 1); }
                              if (event.key === 'ArrowDown' && index < rows.length - 1) { event.preventDefault(); move(index, index + 1); }
                            }}
                            aria-label={`Move ${row.name || 'new stage'} — drag, or use the arrow keys`}
                            className="grid h-9 w-7 cursor-grab place-items-center rounded-md text-ink-4 hover:bg-surface-hover hover:text-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
                            data-testid={`stage-drag-${index}`}
                          >
                            <GripVertical className="h-[15px] w-[15px]" aria-hidden="true" />
                          </button>
                        </td>
                      ) : null}
                      <td className="shrink-0 md:h-[56px] md:px-4 md:align-middle">
                        <span className="grid h-7 w-7 place-items-center rounded-full border border-line text-[12px] font-bold tabular-nums text-ink-2 md:block md:h-auto md:w-auto md:border-0 md:text-left md:text-[13px] md:font-normal md:text-ink-3">
                          {index + 1}
                        </span>
                      </td>
                      <td className="min-w-0 flex-1 basis-[60%] md:px-3 md:py-2 md:align-middle">
                        {editing ? (
                          <div className="space-y-1.5">
                            <div className="relative">
                              <input
                                ref={(node) => { nameRefs.current[row.key] = node; }}
                                value={row.name}
                                onChange={(event) => updateRow(row.key, { name: event.target.value })}
                                onBlur={() => setLeftKeys((current) => (current.has(row.key) ? current : new Set([...current, row.key])))}
                                readOnly={isRequired}
                                maxLength={NAME_MAX}
                                placeholder="Stage name, e.g. Testing & Commissioning"
                                aria-label={`Stage ${index + 1} name`}
                                aria-invalid={Boolean(problem)}
                                className={cn(
                                  'h-8 w-full rounded-control border bg-surface pl-2.5 pr-14 text-[13.5px] font-semibold text-ink placeholder:font-normal placeholder:text-ink-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                  problem ? 'border-[color:var(--danger-border)]' : 'border-line',
                                  isRequired && 'bg-surface-2 text-ink-2'
                                )}
                                data-testid={`stage-name-${index}`}
                              />
                              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] tabular-nums text-ink-3">
                                {isRequired ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="pointer-events-auto inline-flex items-center gap-1" tabIndex={0}>
                                        <Lock className="h-3 w-3" aria-hidden="true" /> Kept
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>New projects need this stage — its name stays so the template keeps working.</TooltipContent>
                                  </Tooltip>
                                ) : `${row.name.length}/${NAME_MAX}`}
                              </span>
                            </div>
                            <input
                              value={row.description}
                              onChange={(event) => updateRow(row.key, { description: event.target.value })}
                              maxLength={300}
                              placeholder="What happens in this stage"
                              aria-label={`Stage ${index + 1} description`}
                              className="h-8 w-full rounded-control border border-line bg-surface px-2.5 text-[12.5px] text-ink-2 placeholder:text-ink-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              data-testid={`stage-description-${index}`}
                            />
                            {problem ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert">{problem}</p> : null}
                          </div>
                        ) : (
                          <>
                            <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[14px] font-semibold text-ink md:text-[13.5px]">
                              <span className="truncate">{row.name}</span>
                              {outOfDate && added.has(row.name) ? <StatusPill tone="green" label="Not in saved template" size="sm" hideDot /> : null}
                              {outOfDate && renamedFrom.has(row.name) ? <StatusPill tone="blue" label={`Renamed from ${renamedFrom.get(row.name)}`} size="sm" hideDot className="max-sm:!h-auto max-sm:!whitespace-normal max-sm:!rounded-lg max-sm:py-0.5 [&>.ui-status-label]:max-sm:whitespace-normal" /> : null}
                            </p>
                            {row.description ? <p className="hidden truncate text-[12px] text-ink-3 md:block" title={row.description}>{row.description}</p> : null}
                            <span className="md:hidden"><ApprovalLabel on={on} /></span>
                            {override ? <p className="text-[11.5px] text-ink-3 md:hidden">Not applied — new projects use the built-in setting</p> : null}
                          </>
                        )}
                      </td>
                      <td className="shrink-0 md:px-3 md:align-middle">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <label className={cn('inline-flex h-11 min-w-[44px] items-center justify-center gap-2.5 md:h-auto md:justify-start', lockSwitch ? 'cursor-not-allowed' : 'cursor-pointer')}>
                              <Switch
                                checked={on}
                                onCheckedChange={() => (editing ? updateRow(row.key, { required_approval: !on }) : toggleApproval(index))}
                                disabled={lockSwitch || (!editing && savingIndex !== -1)}
                                aria-label={`${row.name || 'New stage'}: approval required`}
                                data-testid={`stage-approval-toggle-${index}`}
                              />
                              <span className={cn(!editing && 'hidden md:inline')}><ApprovalLabel on={on} /></span>
                            </label>
                          </TooltipTrigger>
                          {lockSwitch ? <TooltipContent>Adopt the live pipeline first — until then switches here have no effect.</TooltipContent> : null}
                        </Tooltip>
                        {override ? (
                          <span className="hidden items-center gap-1.5 text-[12px] text-ink-3 md:flex" data-testid={`stage-override-${index}`}>
                            <Info className="h-[13px] w-[13px] shrink-0" aria-hidden="true" /> Not applied — new projects use the built-in setting ({override.live ? 'Gate' : 'No approval'})
                          </span>
                        ) : null}
                      </td>
                      <td className="w-full shrink-0 md:w-auto md:px-3 md:align-middle" data-testid={`stage-approver-cell-${index}`}>
                        {on ? (
                          <span className="flex items-center gap-2">
                            <span className="shrink-0 whitespace-nowrap text-[12px] text-ink-3 md:hidden">Approved by</span>
                            {lockSwitch ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {/* a disabled select gets no pointer events: the wrapper carries the tooltip */}
                                  <span className="block w-full min-w-0 cursor-not-allowed" tabIndex={0} data-testid={`stage-approver-locked-${index}`}>
                                    <ApproverSelect
                                      stageName={row.name || 'New stage'}
                                      value={row.approved_by || ''}
                                      approvers={approvers}
                                      onChange={() => {}}
                                      disabled
                                      testId={`stage-approver-${index}`}
                                    />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>Adopt the live pipeline first — until then changes here have no effect.</TooltipContent>
                              </Tooltip>
                            ) : (
                              <ApproverSelect
                                stageName={row.name || 'New stage'}
                                value={row.approved_by || ''}
                                approvers={approvers}
                                onChange={(value) => (editing ? updateRow(row.key, { approved_by: value }) : changeApprover(index, value))}
                                disabled={!editing && savingIndex !== -1}
                                testId={`stage-approver-${index}`}
                              />
                            )}
                          </span>
                        ) : (
                          <span className="hidden text-[13px] text-ink-4 md:inline" aria-label="No approval, no approver">—</span>
                        )}
                      </td>
                      <td className="shrink-0 md:px-3 md:align-middle" data-testid={`stage-sla-cell-${index}`}>
                        {editing ? (
                          <label className="flex items-center gap-2">
                            <span className="text-[12px] text-ink-3 md:hidden">SLA (days)</span>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              max={STAGE_SLA_MAX_DAYS}
                              step={1}
                              value={row.sla_days ?? ''}
                              onChange={(event) => updateRow(row.key, { sla_days: event.target.value })}
                              placeholder={on ? String(DEFAULT_APPROVAL_SLA_DAYS) : '—'}
                              aria-label={`${row.name || 'New stage'}: SLA in days (empty = default, 0 = none)`}
                              className="h-8 w-[76px] rounded-control border border-line bg-surface px-2 text-[13px] tabular-nums text-ink placeholder:text-ink-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              data-testid={`stage-sla-${index}`}
                            />
                          </label>
                        ) : (
                          <span className={cn('whitespace-nowrap text-[13px] tabular-nums', stageSlaDays(row) === null ? 'text-ink-3' : 'text-ink-2')} data-testid={`stage-sla-label-${index}`}>
                            <span className="mr-1.5 text-[12px] text-ink-3 md:hidden">SLA</span>
                            {stageSlaLabel(row)}
                          </span>
                        )}
                      </td>
                      <td className="shrink-0 md:px-3 md:text-right md:align-middle">
                        <span className="mr-1.5 text-[12px] text-ink-3 md:hidden">Active</span>
                        <ActiveProjectsCell
                          stageName={row.origin || row.name}
                          projects={row.isNew ? [] : stageProjects[row.origin] || []}
                          count={row.isNew ? 0 : counts[row.origin] || 0}
                          index={index}
                          isNew={Boolean(row.isNew)}
                        />
                      </td>
                      <td className="ml-auto shrink-0 text-right md:ml-0 md:pr-3 md:align-middle">
                        <RowActions
                          label={`Actions for ${row.name || 'new stage'}`}
                          items={rowMenu(row, index)}
                          className="h-10 w-10 md:h-7 md:w-7"
                          data-testid={`stage-actions-${index}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {outOfDate && sync?.dropped?.length && !editing ? (
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-line px-4 py-3 text-[12.5px] text-ink-3 sm:px-5" data-testid="stage-template-dropped">
              <CircleMinus className="h-3.5 w-3.5" aria-hidden="true" />
              In the saved template but not used by projects:
              {sync.dropped.map((name) => (
                <span key={name} className="rounded-md border border-line bg-surface-2 px-1.5 text-[12px] text-ink-3 line-through">{name}</span>
              ))}
              <span>— handled in Procurement (LPOs, delivery notes, site requests).</span>
            </p>
          ) : null}
        </section>

        <UnsavedBar
          count={dirtyCount}
          detail={`${changes.map((change) => change.text).join(' · ')}${outOfDate ? ' · saving also makes this the template new projects use' : ''}`}
          status={status === 'saved' && dirtyCount === 0 ? 'saved' : status}
          error={saveError || (anyShownProblem ? 'Fix the highlighted stage names first' : '')}
          savedLabel="Pipeline saved"
        >
          <div className="ml-auto flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
            <Button type="button" variant="ghost" onClick={() => addStage()} disabled={status === 'saving'} data-testid="stage-add-bar">
              <Plus aria-hidden="true" /> Add stage
            </Button>
            <Button type="button" variant="ghost" onClick={discard} disabled={status === 'saving'} data-testid="stage-discard">Discard</Button>
            <Button type="button" variant="primary" onClick={savePipeline} disabled={status === 'saving' || hasProblems || !dirtyCount} data-testid="stage-save-pipeline">
              <Check aria-hidden="true" /> {status === 'saving' ? 'Saving…' : status === 'error' ? 'Try again' : 'Save pipeline'}
            </Button>
          </div>
        </UnsavedBar>
      </div>
    </TooltipProvider>
  );
}
